import React, { useEffect, useState, useMemo } from "react";
import Layout from "@/components/Layout";
import { api, colones, fmtFecha, todayISO, weekRange, toLocalISO } from "@/lib/api";
import { MinusCircle, Plus, X, Trash2, Pencil, ChevronLeft, ChevronRight, Calendar, FileDown } from "lucide-react";
import ReportModal from "@/components/ReportModal";
import { reporteRebajos } from "@/lib/reportes";
import { empleadosPorTipo, matchesFrecuencia, empleadoActivoEn } from "@/lib/frecuencia";

const CONCEPTOS = [
  "Adelanto de Salario",
  "Alimentación",
  "Embargo Salarial",
  "Pensión Alimentaria",
  "Uniforme",
  "Caja de Ahorro",
  "Asociación Solidarista",
  "Otro",
];

// === Período Semanal / Quincenal helpers ===
function getPeriodRange(tipo, offset) {
  if (tipo === "semanal") {
    const r = weekRange(offset);
    return { start: r.start, end: r.end, label: `Semana del ${fmtFecha(r.start)} al ${fmtFecha(r.end)}` };
  }
  // Quincenal: alterna 1-15 / 16-fin
  const d = new Date();
  d.setMonth(d.getMonth() + Math.floor(offset / 2));
  const half = ((offset % 2) + 2) % 2 === 0; // 0=primera, 1=segunda
  const y = d.getFullYear(); const m = d.getMonth();
  let start, end;
  if (half) { start = new Date(y, m, 1); end = new Date(y, m, 15); }
  else { start = new Date(y, m, 16); end = new Date(y, m + 1, 0); }
  return {
    start: toLocalISO(start),
    end: toLocalISO(end),
    label: `${half ? "Primera" : "Segunda"} quincena de ${start.toLocaleDateString("es-CR", { month: "long", year: "numeric" })}`,
  };
}

const RebajoModal = ({ rebajo, empleados, onClose, onSaved }) => {
  // conceptos: { "Adelanto de Salario": 5000, "Uniforme": 3000, ... }
  // Cada key marcada tiene su monto. El total del rebajo = suma de los valores.
  const initialConceptos = () => {
    if (rebajo?.conceptos && typeof rebajo.conceptos === "object") return { ...rebajo.conceptos };
    // Compat: rebajo antiguo con un solo tipo + monto → precargamos ese concepto.
    if (rebajo?.tipo && rebajo?.monto) {
      const mappedKey = CONCEPTOS.find(c => c.toLowerCase() === String(rebajo.tipo).toLowerCase()) || "Otro";
      return { [mappedKey]: Number(rebajo.monto) || 0 };
    }
    return {};
  };
  const [data, setData] = useState(() => rebajo || { activo: true, fecha: todayISO() });
  const [conceptos, setConceptos] = useState(initialConceptos);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setData(p => ({ ...p, [k]: v }));

  const total = useMemo(
    () => Object.values(conceptos).reduce((s, v) => s + (Number(v) || 0), 0),
    [conceptos]
  );

  const toggleConcepto = (nombre, checked) => {
    setConceptos(prev => {
      const next = { ...prev };
      if (checked) next[nombre] = prev[nombre] ?? 0;
      else delete next[nombre];
      return next;
    });
  };
  const setMonto = (nombre, monto) => {
    setConceptos(prev => ({ ...prev, [nombre]: Number(monto) || 0 }));
  };

  const empSeleccionado = empleados.find(e => e.id === data.empleado_id);
  const minFecha = empSeleccionado?.fecha_ingreso || undefined;
  const fechaInvalida = empSeleccionado && data.fecha && minFecha && data.fecha < minFecha;

  const save = async (e) => {
    e.preventDefault();
    if (fechaInvalida) {
      setErr(`La fecha no puede ser anterior a la fecha de ingreso del empleado (${minFecha}).`);
      return;
    }
    const claves = Object.keys(conceptos);
    if (claves.length === 0) {
      setErr("Marcá al menos un tipo de rebajo.");
      return;
    }
    if (total <= 0) {
      setErr("El monto total del rebajo debe ser mayor que 0.");
      return;
    }
    setErr(""); setSaving(true);
    // tipo: si es uno solo, usa ese; si son varios, "Múltiple". Se guarda para
    // compatibilidad con vistas antiguas (planilla, reportes, PDFs).
    const tipoLabel = claves.length === 1 ? claves[0] : "Múltiple";
    const payload = {
      ...data,
      tipo: tipoLabel,
      conceptos,
      monto: total,
      fecha_inicio: data.fecha,
      frecuencia: "Único",
    };
    try {
      if (rebajo?.id) await api.put(`/rebajos/${rebajo.id}`, payload);
      else await api.post("/rebajos", payload);
      onSaved();
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl fade-in-up max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="rebajo-modal">
        <div className="p-6 border-b border-c flex justify-between"><h3 className="font-display text-2xl font-semibold">{rebajo ? "Editar rebajo" : "Nuevo rebajo"}</h3><button onClick={onClose} className="np-btn np-btn-ghost p-2"><X size={18} /></button></div>
        <form onSubmit={save} className="p-6 space-y-4">
          <div>
            <label className="np-label">Empleado *</label>
            <select required className="np-input" value={data.empleado_id || ""} onChange={e => set("empleado_id", e.target.value)} data-testid="rebajo-empleado">
              <option value="">Seleccionar empleado</option>
              {empleados.map(e => <option key={e.id} value={e.id}>{e.nombre} {e.apellidos}</option>)}
            </select>
          </div>

          <div>
            <div className="np-label mb-2">Tipos de rebajo *</div>
            <p className="text-xs text-secondary-c mb-3">Marcá los conceptos y digitá el monto de cada uno. El total se suma automáticamente.</p>
            <div className="space-y-2">
              {CONCEPTOS.map(c => {
                const checked = c in conceptos;
                return (
                  <div key={c} className={`p-3 rounded-xl border transition-colors ${checked ? "border-primary-c bg-[#F5FAF7]" : "border-c bg-white"}`}>
                    <label className="flex items-center justify-between gap-3 cursor-pointer">
                      <div className="flex items-center gap-2.5">
                        <input
                          type="checkbox"
                          className="w-4 h-4 accent-[#274735]"
                          checked={checked}
                          onChange={e => toggleConcepto(c, e.target.checked)}
                          data-testid={`rebajo-check-${c.toLowerCase().replace(/\s+/g, "-")}`}
                        />
                        <span className="text-sm font-medium">{c}</span>
                      </div>
                      {checked && (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-secondary-c">₡</span>
                          <input
                            type="number"
                            min={0}
                            value={conceptos[c] || ""}
                            onChange={e => setMonto(c, e.target.value)}
                            onClick={e => e.preventDefault()}
                            className="np-input py-1 px-2 text-sm text-right w-32"
                            placeholder="0"
                            data-testid={`rebajo-monto-${c.toLowerCase().replace(/\s+/g, "-")}`}
                          />
                        </div>
                      )}
                    </label>
                  </div>
                );
              })}
            </div>
            {Object.keys(conceptos).length > 0 && (
              <div className="mt-3 p-3 rounded-xl bg-[#E6EFEA] flex items-center justify-between">
                <span className="text-sm font-semibold text-primary-c">Total rebajo</span>
                <span className="text-lg font-display font-semibold text-primary-c" data-testid="rebajo-total">{colones(total)}</span>
              </div>
            )}
          </div>

          <div>
            <label className="np-label">Descripción</label>
            <input className="np-input" value={data.descripcion || ""} onChange={e => set("descripcion", e.target.value)} placeholder="Detalle del rebajo (opcional)" />
          </div>
          <div>
            <label className="np-label">Fecha del rebajo *</label>
            <input type="date" required min={minFecha} className="np-input" value={data.fecha || ""} onChange={e => set("fecha", e.target.value)} data-testid="rebajo-fecha" />
            <p className="text-xs text-secondary-c mt-1.5">El rebajo se aplicará únicamente al período (semana o quincena) que contenga esta fecha.</p>
            {empSeleccionado?.fecha_ingreso && (
              <p className="text-[11px] text-secondary-c mt-1">Fecha mínima: {empSeleccionado.fecha_ingreso} (ingreso del empleado)</p>
            )}
            {fechaInvalida && (
              <p className="text-xs text-error font-medium mt-1">La fecha es anterior al ingreso del empleado.</p>
            )}
          </div>
          <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={!!data.activo} onChange={e => set("activo", e.target.checked)} /><span className="text-sm">Rebajo activo</span></label>
          {err && <div className="p-3 rounded-xl bg-[#FDEDED] text-[#B84B4B] text-sm">{err}</div>}
          <div className="flex justify-end gap-3 pt-2 border-t border-c">
            <button type="button" onClick={onClose} className="np-btn np-btn-ghost">Cancelar</button>
            <button type="submit" disabled={saving || fechaInvalida} className="np-btn np-btn-primary" data-testid="rebajo-submit">{saving ? "Guardando..." : (rebajo ? "Guardar" : "Crear rebajo")}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default function Rebajos() {
  const [tipo, setTipo] = useState("semanal");
  const [offset, setOffset] = useState(0);
  const [items, setItems] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [filtroEmp, setFiltroEmp] = useState("");
  const [open, setOpen] = useState(null);
  const [showReport, setShowReport] = useState(false);
  const [empresa, setEmpresa] = useState({});

  useEffect(() => { api.get("/empresa").then(r => setEmpresa(r.data || {})).catch(err => console.error("Rebajos: no se pudo cargar empresa", err)); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const generarReporte = async ({ format, fechaInicio, fechaFin, periodoLabel }) => {
    const filtrados = items.filter(r => {
      const f = r.fecha || r.fecha_inicio;
      return f && f >= fechaInicio && f <= fechaFin;
    });
    reporteRebajos({ format, items: filtrados, empleados, periodoLabel, empresa });
  };

  const range = useMemo(() => getPeriodRange(tipo, offset), [tipo, offset]);

  const load = async () => {
    const [r, e] = await Promise.all([api.get("/rebajos"), api.get("/empleados")]);
    setItems(r.data); setEmpleados(e.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const empById = Object.fromEntries(empleados.map(e => [e.id, e]));
  const empleadosFiltrados = useMemo(
    () => empleadosPorTipo(empleados, tipo).filter(e => empleadoActivoEn(e, range.end)),
    [empleados, tipo, range.end]
  );

  const filtered = useMemo(() => {
    return items.filter(r => {
      const emp = empById[r.empleado_id];
      if (!emp || !matchesFrecuencia(emp, tipo)) return false;
      if (filtroEmp && r.empleado_id !== filtroEmp) return false;
      const f = r.fecha || r.fecha_inicio;
      if (!f) return false;
      return f >= range.start && f <= range.end;
    });
  }, [items, filtroEmp, range, empById, tipo]);

  const totalPeriodo = filtered.filter(r => r.activo).reduce((s, r) => s + (Number(r.monto) || 0), 0);

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este rebajo?")) return;
    await api.delete(`/rebajos/${id}`);
    load();
  };

  return (
    <Layout>
      <header className="flex items-end justify-between mb-8 flex-wrap gap-4 fade-in-up">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-[#FDEDED] text-error flex items-center justify-center"><MinusCircle size={26} strokeWidth={1.5} /></div>
          <div>
            <h1 className="font-display text-3xl font-semibold">Rebajos</h1>
            <p className="text-sm text-secondary-c">Gestión de rebajos aplicados a empleados</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowReport(true)} className="np-btn np-btn-outline" data-testid="rebajos-report-btn"><FileDown size={16} className="mr-1.5" />Reporte</button>
          <button onClick={() => setOpen("new")} className="np-btn np-btn-primary" data-testid="new-rebajo-btn"><Plus size={16} /> Nuevo rebajo</button>
        </div>
      </header>

      {showReport && <ReportModal title="Reporte de Rebajos" mode="periodo" onClose={() => setShowReport(false)} onGenerate={generarReporte} />}

      <div className="np-card p-4 mb-5 flex flex-wrap items-center gap-4 justify-between">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="inline-flex rounded-xl bg-[#EFEEE8] p-1">
            <button onClick={() => { setTipo("semanal"); setOffset(0); }} className={`px-4 py-1.5 rounded-lg text-sm font-medium ${tipo === "semanal" ? "bg-white text-primary-c shadow-sm" : "text-secondary-c"}`} data-testid="reb-tab-semanal">Semanal</button>
            <button onClick={() => { setTipo("quincenal"); setOffset(0); }} className={`px-4 py-1.5 rounded-lg text-sm font-medium ${tipo === "quincenal" ? "bg-white text-primary-c shadow-sm" : "text-secondary-c"}`} data-testid="reb-tab-quincenal">Quincenal</button>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setOffset(o => o - 1)} className="np-btn np-btn-outline p-2" data-testid="reb-prev"><ChevronLeft size={16} /></button>
            <div className="flex items-center gap-2 px-4 py-2 bg-[#FAFAF7] rounded-xl text-sm font-medium" data-testid="reb-period-label"><Calendar size={14} /> {range.label}</div>
            <button onClick={() => setOffset(o => o + 1)} className="np-btn np-btn-outline p-2" data-testid="reb-next"><ChevronRight size={16} /></button>
          </div>
          <select className="np-input w-auto" value={filtroEmp} onChange={e => setFiltroEmp(e.target.value)} data-testid="filter-empleado">
            <option value="">Todos los empleados</option>
            {empleadosFiltrados.map(e => <option key={e.id} value={e.id}>{e.nombre} {e.apellidos}</option>)}
          </select>
        </div>
        <div className="np-card !p-3 !shadow-none flex items-center gap-3 text-sm">
          <span className="text-secondary-c">Total período:</span>
          <span className="font-semibold text-error font-display text-lg" data-testid="rebajo-total">{colones(totalPeriodo)}</span>
        </div>
      </div>

      <div className="np-card p-0 np-scroll">
        {filtered.length === 0 ? (
          <div className="p-12 text-center"><MinusCircle size={36} className="mx-auto text-secondary-c opacity-40 mb-3" /><div className="font-medium">No hay rebajos en este período</div><div className="text-sm text-secondary-c mt-1">Probá cambiando el período o creando uno nuevo.</div></div>
        ) : (
          <table className="np-table">
            <thead><tr><th>Empleado</th><th>Tipo</th><th>Descripción</th><th>Fecha</th><th>Monto</th><th>Estado</th><th></th></tr></thead>
            <tbody>
              {filtered.map(r => {
                const emp = empById[r.empleado_id];
                const f = r.fecha || r.fecha_inicio;
                return (
                  <tr key={r.id} data-testid={`rebajo-row-${r.id}`}>
                    <td className="font-medium">{emp ? `${emp.nombre} ${emp.apellidos}` : "—"}</td>
                    <td>
                      {r.conceptos && Object.keys(r.conceptos).length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(r.conceptos).map(([nom, m]) => (
                            <span key={nom} className="np-badge np-badge-info" title={`${nom}: ${colones(m)}`}>
                              {nom} <span className="opacity-70">({colones(m)})</span>
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="np-badge np-badge-info">{r.tipo || "—"}</span>
                      )}
                    </td>
                    <td className="text-secondary-c text-sm">{r.descripcion || "—"}</td>
                    <td className="text-secondary-c text-sm">{fmtFecha(f)}</td>
                    <td className="font-semibold text-error">−{colones(r.monto)}</td>
                    <td><span className={`np-badge ${r.activo ? "np-badge-success" : "np-badge-neutral"}`}>{r.activo ? "Activo" : "Inactivo"}</span></td>
                    <td>
                      <div className="flex gap-2 justify-end">
                        <button onClick={() => setOpen({ rebajo: { ...r, fecha: r.fecha || r.fecha_inicio } })} className="np-btn np-btn-ghost p-2"><Pencil size={15} /></button>
                        <button onClick={() => remove(r.id)} className="np-btn np-btn-ghost p-2 text-error"><Trash2 size={15} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {open && <RebajoModal rebajo={open === "new" ? null : open.rebajo} empleados={empleadosFiltrados} onClose={() => setOpen(null)} onSaved={() => { setOpen(null); load(); }} />}
    </Layout>
  );
}
